import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Divider,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    IconButton,
    Dialog,
    TextField,
    Button,
    MenuItem,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { vendorApi } from "../../../api/api";
import { calculateTotalPrice, calculateSqftPrice } from "../../common/pricecalculator";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useTheme, useMediaQuery } from "@mui/material";


const YourAprtmentPlotsOverview = ({ data, onFlatAdded, refreshKey }) => {

    const totalUnits = data?.TotalUnits || 0;
    const flats = data?.flats || [];

    const navigate = useNavigate();

    // ✅ SAME METHOD AS PROJECT PLAN
    const [selectedFlat, setSelectedFlat] = useState(() => {
        return flats.find(f => f.Status?.toLowerCase() === "approved") || null;
    });

    const [currentImg, setCurrentImg] = useState(0);
    const [openImageModal, setOpenImageModal] = useState(false);

    const [openAddFlat, setOpenAddFlat] = useState(false);
    const [saving, setSaving] = useState(false);

    const [pricePerSqFt, setPricePerSqFt] = useState("");
    const [totalPrice, setTotalPrice] = useState("");
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success", // success | error | warning | info
    });


    const handleSnackbarClose = (_, reason) => {
        if (reason === "clickaway") return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };


    const [newFlat, setNewFlat] = useState({
        ApartmentId: data?.id,
        FlatName: "",
        FlatType: "",
        FloorNo: "",
        SizeInSqFt: "",
        PriceType: "Total",
        Price: "",
        Facing: "",
    });

    const [displayImage, setDisplayImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all"); // all | approved | pending | rejected
    const [searchText, setSearchText] = useState("");


    useEffect(() => {
        if (!flats.length) return;
        const firstApproved =
            flats.find(f => f.Status?.toLowerCase() === "approved") || flats[0];
        setSelectedFlat(firstApproved);
    }, [flats]);

    const images =
        selectedFlat?.Images?.length
            ? selectedFlat.Images
            : ["https://via.placeholder.com/600x400?text=No+Image"];

    const formatIndianPrice = (price) => {
        if (!price) return "-";
        const num = Number(price);
        if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
        if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
        return `₹${num}`;
    };

    const filteredFlats = flats.filter(f => {
        const status = f.Status?.toLowerCase();

        const statusMatch =
            statusFilter === "all" || status === statusFilter;

        const searchMatch =
            !searchText ||
            f.FlatName?.toLowerCase().includes(searchText.toLowerCase());

        return statusMatch && searchMatch;
    });

    const statusPriority = {
        approved: 1,
        pending: 2,
        rejected: 3,
    };

    const sortedFlats = [...filteredFlats].sort((a, b) => {
        const aStatus = a.Status?.toLowerCase();
        const bStatus = b.Status?.toLowerCase();

        return (
            (statusPriority[aStatus] || 99) -
            (statusPriority[bStatus] || 99)
        );
    });

    const flatsMap = new Map(
        sortedFlats.map((f) => {
            const match = f.FlatName?.match(/\d+/);
            const flatNumber = match ? parseInt(match[0], 10) : null;
            return [flatNumber, f];
        })
    );
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));     // <600
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–900
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));     // ≥900

    let columns = 3;

    if (isTablet) {
        columns = 2;
    } else if (isMobile) {
        columns = 2;
    }

    const rows = Math.ceil(totalUnits / columns);




    const renderFlatsGrid = () => {

    const grid = [];
    let row = [];

    const statusPriority = {
        approved: 1,
        pending: 2,
        "in progress": 3,
        rejected: 4,
        sold: 5
    };

    // ✅ Convert map to array and sort by status
    const sortedFlats = Array.from(flatsMap.values()).sort((a, b) => {
        const statusA = a?.Status?.toLowerCase();
        const statusB = b?.Status?.toLowerCase();
        return (statusPriority[statusA] || 99) - (statusPriority[statusB] || 99);
    });

    // ✅ Render existing flats first (sorted)
    sortedFlats.forEach((flat) => {

        const status = flat?.Status?.toLowerCase();

        const isApproved = status === "approved";
        const isPending = status === "pending";
        const isInProgress = status === "in progress";
        const isSold = status === "sold";
        const isRejected = status === "rejected";

        const isSelected = selectedFlat?.id === flat?.id;

        row.push(
            <Box
                key={`flat-${flat.id}`}
                onClick={() => {

                    if (isPending) {
                        setSnackbar({
                            open: true,
                            message: "Admin approval pending. You can view only after approval.",
                            severity: "info",
                        });
                        return;
                    }

                    if (isApproved || isInProgress) {
                        setSelectedFlat(flat);
                    }

                }}
                sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                    border: "2px solid",
                    backgroundColor:
                        isSelected
                            ? "#B3E5FC"
                            : isApproved
                                ? "#D0F0C0"
                                : isPending
                                    ? "#FFF3CD"
                                    : isInProgress
                                        ? "#E1F5FE"
                                        : isRejected
                                            ? "#FFCDD2"
                                            : isSold
                                                ? "#E0E0E0"
                                                : "#fff",

                    borderColor:
                        isSelected
                            ? "#1c272c"
                            : isApproved
                                ? "success.main"
                                : isPending
                                    ? "warning.main"
                                    : isInProgress
                                        ? "info.main"
                                        : isRejected
                                            ? "error.main"
                                            : isSold
                                                ? "grey.600"
                                                : "grey.400",

                    transition: "all 0.2s ease",
                    "&:hover": {
                        transform: "scale(1.08)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    },

                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    m: 1,
                }}
            >
                <Typography fontWeight={600}>{flat.FlatName}</Typography>
                <Typography fontSize={12}>{flat.SizeInSqFt} sqft</Typography>

                <Chip
                    label={flat.Status}
                    size="small"
                    color={
                        isApproved
                            ? "success"
                            : isPending
                                ? "warning"
                                : isInProgress
                                    ? "info"
                                    : isRejected
                                        ? "error"
                                        : isSold
                                            ? "default"
                                            : "default"
                    }
                />
            </Box>
        );

        if (row.length === columns) {
            grid.push(
                <Box key={`row-${grid.length}`} display="flex">
                    {row}
                </Box>
            );
            row = [];
        }
    });

    // ✅ Add empty flats at last
    const existingFlatNumbers = new Set(sortedFlats.map(f => f.FlatNo));

    for (let flatNo = 1; flatNo <= totalUnits; flatNo++) {
        if (!existingFlatNumbers.has(flatNo)) {

            row.push(
                <Box
                    key={`empty-${flatNo}`}
                    onClick={() => {
                        setNewFlat(prev => ({
                            ...prev,
                            FlatName: `Flat ${flatNo}`,
                            ApartmentId: data.id,
                        }));
                        setOpenAddFlat(true);
                    }}
                    sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        border: "2px solid",
                        borderColor: "grey.400",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        m: 1,
                    }}
                >
                    <AddIcon sx={{ color: "green" }} />
                    <Typography fontSize={12}>Add Flat</Typography>
                </Box>
            );

            if (row.length === columns) {
                grid.push(
                    <Box key={`row-${grid.length}`} display="flex">
                        {row}
                    </Box>
                );
                row = [];
            }
        }
    }

    if (row.length) {
        grid.push(
            <Box key="last-row" display="flex">
                {row}
            </Box>
        );
    }

    return grid;
};



    useEffect(() => {
        if (!newFlat.SizeInSqFt) return;

        if (newFlat.PriceType === "PerSqFt" && pricePerSqFt) {
            setTotalPrice(
                calculateTotalPrice(pricePerSqFt, newFlat.SizeInSqFt)
            );
        }

        if (newFlat.PriceType === "Total" && totalPrice) {
            setPricePerSqFt(
                calculateSqftPrice(totalPrice, newFlat.SizeInSqFt)
            );
        }
    }, [newFlat.SizeInSqFt, newFlat.PriceType]);

    const handleSqftPriceChange = (value) => {
        setPricePerSqFt(value);

        if (newFlat.SizeInSqFt) {
            setTotalPrice(
                calculateTotalPrice(value, newFlat.SizeInSqFt)
            );
        }
    };

    const handleTotalPriceChange = (value) => {
        setTotalPrice(value);

        if (newFlat.SizeInSqFt) {
            setPricePerSqFt(
                calculateSqftPrice(value, newFlat.SizeInSqFt)
            );
        }
    };

    const handleGalleryChange = (e) => {
        const selected = Array.from(e.target.files);
        setGalleryFiles(prev => [...prev, ...selected]);
        setGalleryPreviews(prev => [
            ...prev,
            ...selected.map(f => URL.createObjectURL(f))
        ]);
    };



    const handleSaveFlat = async () => {
        if (!newFlat.FlatName || !newFlat.FlatType || !displayImage) {
            setSnackbar({
                open: true,
                message: "Please fill all required fields",
                severity: "warning",
            });
            return;
        }

        const fd = new FormData();
        fd.append("ApartmentId", newFlat.ApartmentId);
        fd.append("FlatName", newFlat.FlatName);
        fd.append("FlatType", newFlat.FlatType);
        fd.append("FloorNo", newFlat.FloorNo);
        fd.append("SizeInSqFt", newFlat.SizeInSqFt);
        fd.append("PriceType", newFlat.PriceType);
        fd.append(
            "Price",
            newFlat.PriceType === "PerSqFt" ? pricePerSqFt : totalPrice
        );
        fd.append("Facing", newFlat.Facing);
        fd.append("DisplayImage", displayImage);
        galleryFiles.forEach(img => fd.append("images[]", img));

        try {
            setSaving(true);

            await vendorApi.vendorAddFlat(fd);

            setSnackbar({
                open: true,
                message: "Flat added successfully",
                severity: "success",
            });

            setOpenAddFlat(false);

            setTimeout(() => {
                navigate(-1);
            }, 2000);


        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to add flat. Please try again.",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };
    return (
        <>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>

                {/* ================= LEFT : FLATS ================= */}
                <Paper sx={{ width: { xs: "98%", md: "45%" }, height: 480, overflowY: "auto" }}>
                    <Box p={2}>


                        <Grid container spacing={1}>
                            {/* <Typography fontWeight={700} mb={1}>
                                Flats
                            </Typography> */}
                            {/* STATUS FILTER */}
                            <Grid xs={6}>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    label="Status"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                </TextField>
                            </Grid>

                            {/* SEARCH */}
                            <Grid xs={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Search Flat"
                                    placeholder="Flat 1, Flat 2..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    sx={{
                                        maxWidth: {
                                            xs: "10rem",
                                            lg: "100%"
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>


                    <Box
                        sx={{
                            height: 400,
                            overflowY: "auto",
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            // gap:1,


                        }}
                    >
                        {renderFlatsGrid()}
                    </Box>

                </Paper>

                {/* ================= RIGHT : DETAILS ================= */}
                <Paper sx={{ width: { xs: "100%", md: "60%" }, p: 2 }}>
                    {selectedFlat && (
                        <>
                            {/* IMAGE */}
                            <Box sx={{ position: "relative" }}>
                                <Box
                                    component="img"
                                    src={images[currentImg]}
                                    onClick={() => setOpenImageModal(true)}
                                    sx={{
                                        width: "100%",
                                        height: 250,
                                        objectFit: "cover",
                                        borderRadius: 2,
                                        cursor: "pointer",
                                    }}
                                />

                                {images.length > 0 && (
                                    <>
                                        <IconButton
                                            onClick={() =>
                                                setCurrentImg(prev => prev === 0 ? images.length - 1 : prev - 1)
                                            }
                                            sx={{ position: "absolute", top: "45%", left: 10, bgcolor: "white" }}
                                        >
                                            <KeyboardArrowUpIcon sx={{ transform: "rotate(-90deg)" }} />
                                        </IconButton>

                                        <IconButton
                                            onClick={() =>
                                                setCurrentImg(prev => prev === images.length - 1 ? 0 : prev + 1)
                                            }
                                            sx={{ position: "absolute", top: "45%", right: 10, bgcolor: "white" }}
                                        >
                                            <KeyboardArrowDownIcon sx={{ transform: "rotate(-90deg)" }} />
                                        </IconButton>
                                    </>
                                )}
                            </Box>

                            <Typography variant="h6" mt={2}>
                                {selectedFlat.FlatName}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={1}>
                                {[
                                    { label: "Type", value: selectedFlat.FlatType },
                                    { label: "Facing", value: selectedFlat.Facing },
                                    { label: "Floor", value: selectedFlat.FloorNo },
                                    { label: "Size", value: `${selectedFlat.SizeInSqFt} SqFt` },
                                    { label: "Price", value: formatIndianPrice(selectedFlat.Price) },
                                ].map((item, i) => (
                                    <Grid key={i}>
                                        <Box
                                            sx={{
                                                border: "1px solid #ccc",
                                                borderRadius: 2,
                                                p: 1.5,
                                                minWidth: 120,
                                            }}
                                        >
                                            <Typography fontSize={13} color="text.secondary">
                                                {item.label}
                                            </Typography>
                                            <Typography fontWeight={600}>
                                                {item.value || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Paper>
            </Box>

            {/* ================= IMAGE MODAL ================= */}
            <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="lg" fullWidth>
                <Box sx={{ position: "relative", bgcolor: "black" }}>
                    <IconButton
                        onClick={() => setOpenImageModal(false)}
                        sx={{ position: "absolute", top: 10, right: 10, color: "white" }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box
                        component="img"
                        src={images[currentImg]}
                        sx={{ width: "100%", height: "80vh", objectFit: "contain" }}
                    />
                </Box>
            </Dialog>

            <Dialog open={openAddFlat} onClose={() => setOpenAddFlat(false)} maxWidth="sm" fullWidth>
                <Box p={3}>
                    <Typography variant="h6" mb={3}>Add New Flat</Typography>

                    <Box sx={{ overflowY: 'auto', pr: 1 }}>
                        <Box mb={3} mt={1}>
                            <TextField
                                label="Flat Name"
                                fullWidth
                                value={newFlat.FlatName}
                                onChange={e => setNewFlat({ ...newFlat, FlatName: e.target.value })}

                            />
                        </Box>

                        <Box mb={3}>
                            <TextField
                                select
                                label="Flat Type"
                                fullWidth
                                value={newFlat.FlatType}
                                onChange={e => setNewFlat({ ...newFlat, FlatType: e.target.value })}

                            >
                                {["1BHK", "2BHK", "3BHK", "4BHK", "5BHK", "6BHK", "7BHK", "8BHK", "9BHK", "10BHK"].map(v => (
                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Box mb={3}>
                            <TextField
                                label="Floor No"
                                type="number"
                                fullWidth
                                value={newFlat.FloorNo}
                                onChange={e => setNewFlat({ ...newFlat, FloorNo: e.target.value })}

                            />
                        </Box>

                        <Box mb={3}>
                            <TextField
                                label="Size (SqFt)"
                                type="number"
                                fullWidth
                                value={newFlat.SizeInSqFt}
                                onChange={e => setNewFlat({ ...newFlat, SizeInSqFt: e.target.value })}

                            />
                        </Box>

                        <Box mb={3}>
                            <TextField
                                select
                                label="Price Type"
                                fullWidth
                                value={newFlat.PriceType}
                                onChange={e => setNewFlat({ ...newFlat, PriceType: e.target.value })}

                            >
                                <MenuItem value="Total">Total Price</MenuItem>
                                <MenuItem value="PerSqFt">Price per SqFt</MenuItem>
                            </TextField>
                        </Box>

                        <Box mb={3}>
                            {newFlat.PriceType === "PerSqFt" ? (
                                <TextField
                                    label="Price per SqFt"
                                    fullWidth
                                    value={pricePerSqFt}
                                    onChange={e => handleSqftPriceChange(e.target.value)}
                                />
                            ) : (
                                <TextField
                                    label="Total Price"
                                    fullWidth
                                    value={totalPrice}
                                    onChange={e => handleTotalPriceChange(e.target.value)}
                                />
                            )}
                        </Box>
                        <Box mb={2}>
                            {newFlat.PriceType === "PerSqFt" ? (
                                <Typography variant="body2">
                                    <strong>Total:</strong> {totalPrice || "—"}
                                </Typography>
                            ) : (
                                <Typography variant="body2">
                                    <strong>Per SqFt:</strong> {pricePerSqFt || "—"}
                                </Typography>
                            )}
                        </Box>


                        <Box mb={3}>
                            <TextField
                                label="Facing"
                                fullWidth
                                value={newFlat.Facing}
                                onChange={e => setNewFlat({ ...newFlat, Facing: e.target.value })}

                            />
                        </Box>

                        {/* DISPLAY IMAGE */}
                        <Box mb={3}>
                            <Typography variant="body2" fontWeight="medium" mb={1}>
                                Display Image
                            </Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                fullWidth
                                sx={{ justifyContent: 'flex-start' }}
                            >
                                Upload Display Image
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={e => {
                                        setDisplayImage(e.target.files[0]);
                                        setPreview(URL.createObjectURL(e.target.files[0]));
                                    }}
                                />
                            </Button>

                            {preview && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        style={{
                                            width: '100%',
                                            maxWidth: '200px',
                                            height: '150px',
                                            objectFit: 'cover',
                                            borderRadius: 8
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* GALLERY */}
                        <Box mb={3}>
                            <Typography variant="body2" fontWeight="medium" mb={1}>
                                Gallery Images
                            </Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                fullWidth
                                sx={{ justifyContent: 'flex-start' }}
                            >
                                Upload Gallery Images
                                <input
                                    hidden
                                    multiple
                                    type="file"
                                    accept="image/*"
                                    onChange={handleGalleryChange}
                                />
                            </Button>

                            {galleryPreviews.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="caption" color="textSecondary" mb={1}>
                                        {galleryPreviews.length} image(s) selected
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {galleryPreviews.map((img, i) => (
                                            <img
                                                key={i}
                                                src={img}
                                                alt={`Gallery ${i + 1}`}
                                                style={{
                                                    width: '80px',
                                                    height: '80px',
                                                    objectFit: 'cover',
                                                    borderRadius: 6
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={() => setOpenAddFlat(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveFlat} disabled={saving}>
                            Save Flat
                        </Button>
                    </Box>
                </Box>
            </Dialog>


            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>


        </>
    );
};

export default YourAprtmentPlotsOverview;
